import { useEffect } from "react";

/**
 * Lightweight SEO helper: sets <title>, meta description, og tags.
 * Pure React useEffect — no external lib needed. Only writes; no cleanup necessary
 * because the next page mounting will overwrite the same meta tags.
 */
const setMeta = (selector, attr, value) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const [, name] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
    if (selector.includes("name=")) tag.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
    if (selector.includes("property=")) tag.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const setJsonLd = (id, json) => {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(json);
};

export default function useSEO({
  title,
  description,
  keywords,
  image,
  canonical,
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
    if (title) setMeta('meta[property="og:title"]', "content", title);
    if (description) setMeta('meta[property="og:description"]', "content", description);
    if (image) setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    if (title) setMeta('meta[name="twitter:title"]', "content", title);
    if (description) setMeta('meta[name="twitter:description"]', "content", description);
    if (image) setMeta('meta[name="twitter:image"]', "content", image);

    if (canonical) setLink("canonical", canonical);
    if (jsonLd) setJsonLd(jsonLd.id || "page-jsonld", jsonLd.data || jsonLd);
  }, [title, description, keywords, image, canonical, jsonLd]);
}
